import { CoinIcon } from '../components/ui/CoinIcon';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Search, Coins, Plus, CheckCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => new Intl.NumberFormat('id-ID').format(n);

export const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [topupModal, setTopupModal] = useState(null); // user yang sedang di-topup
  const [topupAmount, setTopupAmount] = useState(10);
  const [topupLoading, setTopupLoading] = useState(false);
  const [topupNote, setTopupNote] = useState('');

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(data.filter(u => u.role === 'guru'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleManualTopup = async () => {
    if (!topupModal || topupAmount <= 0) return;
    setTopupLoading(true);
    try {
      await axios.post(`${API}/coins/admin-topup`, {
        user_id: topupModal._id,
        coins: topupAmount,
        note: topupNote || `Topup manual oleh admin — ${topupAmount} koin`,
      }, { withCredentials: true });
      await loadUsers();
      setTopupModal(null);
      setTopupAmount(10);
      setTopupNote('');
    } catch (e) {
      alert(e.response?.data?.detail || 'Gagal topup');
    } finally {
      setTopupLoading(false);
    }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-blue-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Manajemen Guru
            </h1>
            <p className="text-slate-500 text-sm mt-1">{users.length} guru terdaftar</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Cari nama atau email guru..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <Users size={40} className="mx-auto mb-3 opacity-30" />
              <p>Belum ada guru terdaftar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left px-5 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Guru</th>
                    <th className="text-left px-5 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Email</th>
                    <th className="text-center px-5 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Coin Guru</th>
                    <th className="text-center px-5 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Status</th>
                    <th className="text-center px-5 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Daftar</th>
                    <th className="text-center px-5 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map(u => (
                    <tr key={u._id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                            {u.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <span className="font-semibold text-slate-800 text-sm">{u.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-500">{u.email}</td>
                      <td className="px-5 py-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                          (u.coin_balance || 0) <= 5
                            ? 'bg-red-50 text-red-600'
                            : 'bg-yellow-50 text-yellow-700'
                        }`}>
                          <span className="inline-flex items-center gap-1"><CoinIcon size={13} /> {fmt(u.coin_balance || 0)}</span>
                        </span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        {u.email_verified ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700">
                            <CheckCircle size={11} /> Terverifikasi
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-orange-50 text-orange-600">
                            <Clock size={11} /> Belum verifikasi
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-center text-xs text-slate-400">
                        {new Date(u.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-5 py-4 text-center">
                        <button
                          onClick={() => setTopupModal(u)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all hover:shadow-md hover:-translate-y-0.5"
                          style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                          <Plus size={12} /> Tambah Koin
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal Topup */}
        {topupModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)' }}
            onClick={e => e.target === e.currentTarget && setTopupModal(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <h3 className="font-bold text-slate-800 text-lg mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Tambah Coin Guru
              </h3>
              <p className="text-sm text-slate-500 mb-5">untuk <span className="font-semibold text-blue-700">{topupModal.name}</span></p>

              <div className="mb-4">
                <label className="text-sm font-semibold text-slate-700 block mb-2">Jumlah Koin</label>
                <div className="flex gap-2 mb-2">
                  {[5, 10, 20, 50].map(n => (
                    <button key={n} onClick={() => setTopupAmount(n)}
                      className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${
                        topupAmount === n
                          ? 'text-white shadow-md'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                      style={topupAmount === n ? { background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' } : {}}>
                      {n}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min={1}
                  value={topupAmount}
                  onChange={e => setTopupAmount(parseInt(e.target.value) || 0)}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Jumlah koin custom..."
                />
              </div>

              <div className="mb-5">
                <label className="text-sm font-semibold text-slate-700 block mb-2">Keterangan <span className="text-slate-400 font-normal">(opsional)</span></label>
                <input
                  type="text"
                  value={topupNote}
                  onChange={e => setTopupNote(e.target.value)}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="Contoh: Kompensasi gangguan sistem..."
                />
              </div>

              <div className="flex gap-3">
                <button onClick={() => setTopupModal(null)}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
                  Batal
                </button>
                <button onClick={handleManualTopup} disabled={topupLoading || topupAmount <= 0}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:shadow-md disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
                  {topupLoading ? 'Memproses...' : `Tambah ${topupAmount} Koin`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
